import apiClient, { unwrap } from './client';
import type {
  ApiEnvelope,
  ArtifactsResponse,
  CreateDocumentInput,
  DocumentDetail,
  DocumentListItem,
  DocumentListParams,
  DocumentListResult,
  DocumentType,
  RetryPdfResult,
  UpdateDocumentInput,
} from '@types';

export const documentsApi = {
  /**
   * List documents (F8.1). Server sorts newest-first by default and applies the
   * type/status/client_id/date_from/date_to/q/page filters. Returns the items
   * plus pagination metadata (from the envelope `meta.pagination`).
   */
  list: async (
    params: DocumentListParams = {}
  ): Promise<DocumentListResult> => {
    const envelope = await apiClient.get<ApiEnvelope<DocumentListItem[]>>(
      '/v1/documents',
      { params }
    );
    const pagination = envelope.meta?.pagination ?? {
      page: params.page ?? 1,
      page_size: envelope.data.length,
      total_items: envelope.data.length,
      total_pages: 1,
      has_next: false,
      has_prev: false,
    };
    return { items: envelope.data, pagination };
  },

  /** Full document detail (F3.5/F5/F7.3). */
  get: async (id: string): Promise<DocumentDetail> => {
    const envelope = await apiClient.get<ApiEnvelope<DocumentDetail>>(
      `/v1/documents/${id}`
    );
    return unwrap(envelope);
  },

  /** Create a DRAFT document (F3.1). Returns the created detail. */
  create: async (input: CreateDocumentInput): Promise<DocumentDetail> => {
    const envelope = await apiClient.post<ApiEnvelope<DocumentDetail>>(
      '/v1/documents',
      input
    );
    return unwrap(envelope);
  },

  /**
   * Edit a document. For DRAFT this is a free edit (no version); for
   * ISSUED|ACCEPTED it is edit-and-regenerate → a new R<n> version (F5.3).
   * The server returns the updated detail (with the new current_version_no).
   */
  update: async (
    id: string,
    input: UpdateDocumentInput
  ): Promise<DocumentDetail> => {
    const envelope = await apiClient.put<ApiEnvelope<DocumentDetail>>(
      `/v1/documents/${id}`,
      input
    );
    return unwrap(envelope);
  },

  /** Delete a DRAFT (F5.2). */
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/documents/${id}`);
  },

  /** Issue → assigns the legal doc_no and creates version 1 (F4.4/F5.1). */
  issue: (id: string) => action(id, 'issue'),
  accept: (id: string) => action(id, 'accept'),
  decline: (id: string) => action(id, 'decline'),
  markPaid: (id: string) => action(id, 'mark-paid'),

  /** Void with a mandatory reason (F5.4/F5.7). */
  void: async (id: string, reason: string): Promise<DocumentDetail> => {
    const envelope = await apiClient.post<ApiEnvelope<DocumentDetail>>(
      `/v1/documents/${id}/void`,
      { reason }
    );
    return unwrap(envelope);
  },

  /**
   * Convert a quote → a NEW linked invoice (F5.5). The response is the detail of
   * the newly created invoice — navigate to `result.id`.
   */
  convert: async (id: string): Promise<DocumentDetail> => {
    const envelope = await apiClient.post<ApiEnvelope<DocumentDetail>>(
      `/v1/documents/${id}/convert`
    );
    return unwrap(envelope);
  },

  /** Duplicate → a new DRAFT copying content, never number/status/links (F3.7). */
  duplicate: async (
    id: string,
    type?: DocumentType
  ): Promise<DocumentDetail> => {
    const envelope = await apiClient.post<ApiEnvelope<DocumentDetail>>(
      `/v1/documents/${id}/duplicate`,
      type ? { type } : {}
    );
    return unwrap(envelope);
  },

  /** Signed, short-lived artifact URLs for every version (F6.6). */
  artifacts: async (id: string): Promise<ArtifactsResponse> => {
    const envelope = await apiClient.get<ApiEnvelope<ArtifactsResponse>>(
      `/v1/documents/${id}/artifacts`
    );
    return unwrap(envelope);
  },

  /**
   * Retry PDF conversion for a version without creating a new version (F6.5).
   * Throws on 409 PDF_ALREADY_SET / 502 CONVERSION_FAILED — the caller inspects
   * the error code (getApiErrorCode) to message the user.
   */
  retryPdf: async (id: string, versionNo: number): Promise<RetryPdfResult> => {
    const envelope = await apiClient.post<ApiEnvelope<RetryPdfResult>>(
      `/v1/documents/${id}/versions/${versionNo}/retry-pdf`
    );
    return unwrap(envelope);
  },
};

async function action(
  id: string,
  verb: 'issue' | 'accept' | 'decline' | 'mark-paid'
): Promise<DocumentDetail> {
  const envelope = await apiClient.post<ApiEnvelope<DocumentDetail>>(
    `/v1/documents/${id}/${verb}`
  );
  return unwrap(envelope);
}

export default documentsApi;
